import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  Col,
  Form,
  Input,
  message,
  Radio,
  Row,
  Select,
  Typography,
} from "antd";
import { useForm } from "antd/es/form/Form";
import { useNavigate, useParams } from "react-router";
import { useEffect, useState } from "react";
import { announcementService } from "../../services/announcement.service";
import CardFooterAction from "../../components/CardFooterAction";
import ImageUploader from "../../components/ImageUpload";
import CustomTextInput from "../../components/CustomTextInput";
import CustomTextArea from "../../components/CustomTextArea";
import { useGetDetailApi } from "../../hooks/useApi";
import { useMe } from "../../hooks/useMe";
import { directorateOptions } from "../../data/dummyUser";

const { Text } = Typography;

export default function AnnouncementForm() {
  const { id } = useParams();
  const [form] = useForm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [bannerUrl, setBannerUrl] = useState(null);

  const { data: userDataProfile } = useMe();
  const userDirectorate = userDataProfile?.data?.directorate;
  const userPosition = userDataProfile?.data?.position;
  const isSuperadmin = userPosition?.toLowerCase() === "superadmin";

  const { data, isLoading } = useGetDetailApi({
    id,
    serviceFn: announcementService.findById,
    queryKey: ["announcement"],
  });

  useEffect(() => {
    if (id && data) {
      form.setFieldsValue({
        title: data.title,
        content: data.content,
        category: data.category || "info",
        directorate: data.directorate,
        isPinned: data.isPinned === true || data.isPinned === "true",
        isActive: data.isActive === true || data.isActive === "true",
      });
      if (data.banner) {
        setBannerUrl(data.banner);
      }
    }
  }, [form, id, data]);

  const storeMutation = useMutation({
    mutationFn: (formData) => announcementService.store(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcement"] });
      message.success("Pengumuman berhasil dibuat");
      form.resetFields();
      navigate("/announcement");
    },
    onError: (error) => {
      console.error(error);
      const errMsg =
        error?.response?.data?.message ||
        "Terjadi kesalahan saat menyimpan pengumuman";
      message.error(errMsg);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, formData }) => announcementService.update(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcement"] });
      message.success("Pengumuman berhasil diperbarui");
      form.resetFields();
      navigate("/announcement");
    },
    onError: (error) => {
      console.error(error);
      const errMsg =
        error?.response?.data?.message ||
        "Terjadi kesalahan saat memperbarui pengumuman";
      message.error(errMsg);
    },
  });

  const isPending = storeMutation.isPending || updateMutation.isPending;

  const handleSubmit = (values) => {
    const bannerFile = form.getFieldValue("banner");
    const formData = new FormData();

    Object.entries(values).forEach(([key, value]) => {
      if (key !== "banner" && value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });

    if (bannerFile instanceof File) {
      if (!id) {
        formData.append("banner", bannerFile);
      } else {
        formData.append("newBanner", bannerFile);
      }
    }

    if (!id) {
      storeMutation.mutate(formData);
    } else {
      updateMutation.mutate({
        id,
        formData,
      });
    }
  };

  return (
    <Card
      loading={id ? isLoading : false}
      title={(id ? "Ubah" : "Tambah") + " Pengumuman"}
      actions={[
        <CardFooterAction
          key="actions"
          route="/announcement"
          onClick={() => form.submit()}
          loading={isPending}
          primaryText="Simpan"
          secondaryText="Batal"
        />,
      ]}
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={handleSubmit}
        initialValues={{
          category: "info",
          isActive: true,
          isPinned: false,
          directorate: isSuperadmin
            ? undefined
            : userDirectorate === "Direktorat Vokasi"
              ? userDirectorate
              : "Direktorat Peningkatan Kapasitas",
        }}
      >
        <Form.Item hidden name="uuid">
          <Input />
        </Form.Item>

        <Col xs={24} md={12} style={{ marginBottom: 15 }}>
          <ImageUploader
            form={form}
            name="banner"
            label="Banner Pengumuman"
            imageFromAPI={bannerUrl}
          />
          <Text
            type="secondary"
            style={{
              fontSize: "12px",
              display: "block",
              wordBreak: "break-word",
            }}
          >
            Format file .jpg, .png, atau .webp. Maksimal 2MB.
          </Text>
        </Col>

        <CustomTextInput
          label="Judul Pengumuman"
          name="title"
          rules={[{ required: true, message: "Judul pengumuman harus diisi" }]}
          disabled={isPending}
          inputProps={{
            placeholder: "Masukkan judul pengumuman",
          }}
        />
        <Form.Item
          name="category"
          label={
            <Text>
              Kategori
              <Text style={{ color: "#940808" }}> *</Text>
            </Text>
          }
          required={false}
          rules={[{ required: true, message: "Kategori harus dipilih" }]}
        >
          <Select
            size="large"
            placeholder="Pilih Kategori"
            options={[
              { label: "Informasi", value: "info" },
              { label: "Penting", value: "penting" },
              { label: "Pemberitahuan", value: "pemberitahuan" },
              { label: "Kegiatan", value: "kegiatan" },
            ]}
            disabled={isPending}
          />
        </Form.Item>

        <CustomTextArea
          label="Konten Pengumuman"
          name="content"
          rules={[{ required: true, message: "Konten pengumuman harus diisi" }]}
          showCharCount
          disabled={isPending}
          inputProps={{
            placeholder: "Tulis isi pengumuman secara lengkap...",
            rows: 6,
          }}
        />

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="isPinned"
              label={
                <Text>
                  Pin pengumuman
                  <Text style={{ color: "#940808" }}> *</Text>
                </Text>
              }
              required={false}
              rules={[{ required: true, message: "Status pin harus dipilih" }]}
            >
              <Radio.Group disabled={isPending} size="large">
                <Radio style={{ marginRight: 40 }} value={true}>
                  Ya
                </Radio>
                <Radio value={false}>Tidak</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="isActive"
              label={
                <Text>
                  Status Publikasi
                  <Text style={{ color: "#940808" }}> *</Text>
                </Text>
              }
              required={false}
              rules={[
                { required: true, message: "Status publikasi harus dipilih" },
              ]}
              style={{ marginTop: 10 }}
            >
              <Radio.Group disabled={isPending}>
                <Radio style={{ marginRight: 60 }} value={true}>
                  Active
                </Radio>
                <Radio value={false}>Inactive</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="directorate"
          label={
            <Text>
              Direktorat
              <Text style={{ color: "#940808" }}> *</Text>
            </Text>
          }
          required={false}
          rules={[{ required: true, message: "Direktorat harus diisi" }]}
        >
          <Select
            size="large"
            placeholder="Pilih Direktorat"
            options={directorateOptions}
            disabled={!isSuperadmin}
          />
        </Form.Item>
      </Form>
    </Card>
  );
}
